import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Card,
    Typography,
    Space,
    Descriptions,
    Tag,
    Timeline,
    Badge,
    Row,
    Col,
    Empty
} from 'antd';
import {
    UserOutlined,
    MailOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    MinusCircleOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import { ISurveyResponse, ISurvey } from '../../services/nestjs-base-api/types';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { getDisplayName } from '../../utils/elementDisplayNames';

dayjs.extend(duration);

const { Title, Text, Paragraph } = Typography;

interface ResponseDetailProps {
    response: ISurveyResponse;
    survey: ISurvey;
}

const ResponseDetail: React.FC<ResponseDetailProps> = ({ response, survey }) => {
    const getStatus = (response: ISurveyResponse) => {
        if (response.questionResponses.length === 0) {
            return 'abandoned';
        }
        return 'completed';
    };

    const getStatusColor = (response: ISurveyResponse) => {
        const status = getStatus(response);
        switch (status) {
            case 'completed':
                return 'success';
            case 'abandoned':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (response: ISurveyResponse) => {
        const status = getStatus(response);
        switch (status) {
            case 'completed':
                return <CheckCircleOutlined />;
            case 'abandoned':
                return <MinusCircleOutlined />;
            default:
                return <ClockCircleOutlined />;
        }
    };

    const getResponseValueDisplay = (questionResponse: any) => {
        if (questionResponse.numericValue !== null) {
            return questionResponse.numericValue.toString();
        }
        if (questionResponse.textValue !== null) {
            return questionResponse.textValue;
        }
        return 'No response';
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                        <Space direction="vertical" size="small">
                            <Text type="secondary">
                                <FormattedMessage id="survey.results.detail.participant" />
                            </Text>
                            <Space>
                                {response.participantIdentifier ? (
                                    <Text strong>
                                        <UserOutlined style={{ marginRight: 8 }} />
                                        {response.participantIdentifier}
                                    </Text>
                                ) : (
                                    <Text type="secondary">
                                        <UserOutlined style={{ marginRight: 8 }} />
                                        <FormattedMessage id="survey.results.detail.anonymous" />
                                    </Text>
                                )}
                            </Space>
                            {response.participantEmail && (
                                <Space>
                                    <Text>
                                        <MailOutlined style={{ marginRight: 8 }} />
                                        {response.participantEmail}
                                    </Text>
                                </Space>
                            )}
                        </Space>
                    </Col>
                    <Col xs={24} md={12}>
                        <Space direction="vertical" size="small">
                            <Text type="secondary">
                                <FormattedMessage id="survey.results.detail.status" />
                            </Text>
                            <Tag color={getStatusColor(response)} icon={getStatusIcon(response)}>
                                <FormattedMessage id={`survey.results.status.${getStatus(response)}`} />
                            </Tag>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card>
                <Descriptions
                    title={<FormattedMessage id="survey.results.detail.sessionInfo" />}
                    bordered
                    column={{ xs: 1, sm: 2, md: 2 }}
                    size="small"
                >
                    <Descriptions.Item
                        label={
                            <Space>
                                <CalendarOutlined />
                                <FormattedMessage id="survey.results.detail.submittedAt" />
                            </Space>
                        }
                    >
                        <Space direction="vertical" size={0}>
                            <Text>{dayjs(response.submittedAt).format('MMMM D, YYYY')}</Text>
                            <Text type="secondary">{dayjs(response.submittedAt).format('h:mm:ss A')}</Text>
                        </Space>
                    </Descriptions.Item>
                    
                    <Descriptions.Item
                        label={<FormattedMessage id="survey.results.detail.questionsAnswered" />}
                    >
                        <Badge
                            count={response.questionResponses.length}
                            style={{ backgroundColor: '#52c41a' }}
                        />
                    </Descriptions.Item>

                    <Descriptions.Item
                        label={<FormattedMessage id="survey.results.detail.ipAddress" />}
                    >
                        <Text type="secondary">{response.ipAddress}</Text>
                    </Descriptions.Item>
                    
                    <Descriptions.Item
                        label={<FormattedMessage id="survey.results.detail.browser" />}
                    >
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {response.userAgent}
                        </Text>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card>
                <Title level={4}>
                    <FormattedMessage id="survey.results.detail.responses" />
                </Title>
                
                {response.questionResponses.length === 0 ? (
                    <Empty
                        description={<FormattedMessage id="survey.results.detail.noResponses" />}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : (
                    <Timeline
                        items={response.questionResponses.map((questionResponse, index) => ({
                            color: 'blue',
                            dot: <CheckCircleOutlined />,
                            children: (
                                <Card size="small" style={{ marginBottom: '8px' }}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Text strong>{questionResponse.question.text}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            {getDisplayName(questionResponse.question.category)} • {questionResponse.question.code}
                                        </Text>
                                        
                                        <Space direction="vertical" size="small">
                                            <div>
                                                <Text type="secondary">
                                                    <FormattedMessage id="survey.results.detail.answer" />:
                                                </Text>
                                                <Paragraph 
                                                    style={{ 
                                                        marginLeft: '8px', 
                                                        marginBottom: '8px',
                                                        padding: '8px',
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: '4px'
                                                    }}
                                                >
                                                    {getResponseValueDisplay(questionResponse)}
                                                </Paragraph>
                                            </div>
                                            
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                                <FormattedMessage
                                                    id="survey.results.detail.answeredAt"
                                                    values={{
                                                        time: dayjs(questionResponse.createdAt).format('h:mm:ss A')
                                                    }}
                                                />
                                            </Text>
                                        </Space>
                                    </Space>
                                </Card>
                            ),
                        }))}
                    />
                )}
            </Card>
        </Space>
    );
};

export default ResponseDetail;